import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {ObjectTypes, ApiErrorCodes} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {ServerGroupGetListSchema, ServerStatusValues} from '../../../api/DBSchema/Server';
import store from '../../../store';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {Link} from 'react-router-dom';
import missingStatusIcon from '../../../assets/images/server/led_process_Missing.png';
import okStatusIcon from '../../../assets/images/server/led_process_OK.png';
import warningStatusIcon from '../../../assets/images/server/led_process_Warning.png';
import criticalStatusIcon from '../../../assets/images/server/led_process_Critical.png';
import errorStatusIcon from '../../../assets/images/server/led_stat_error.png';
import disconnectedStatusIcon from '../../../assets/images/server/led_process_Disconnected.png';
import disabledStatusIcon from '../../../assets/images/gateway/disabled.png';
import DescriptionCell from '../../../components/core/SMCTable/CommonCells/DescriptionCell';
import {useSelector} from 'react-redux';
import {isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';

const ServerGetListSchema = ServerGroupGetListSchema.serversSchema;

const NameCell = React.memo(({value}) => {
    if (value) {
        return (
            <Link to={`?id=${value}`} ><SMCIcon icon="edit" align="absmiddle" /> {value}</Link>
        )
    }
    return null;
});

const StatusCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case ServerStatusValues.ok:
            return <img src={okStatusIcon} alt="ok" title={t('SMC_SERVER_PROCESS_STATUS_OK')} />;
        case ServerStatusValues.warning:
            return <img src={warningStatusIcon} alt="warning" title={t('SMC_SERVER_PROCESS_STATUS_WARNING')}/>;
        case ServerStatusValues.critical:
            return <img src={criticalStatusIcon} alt="critical" title={t('SMC_SERVER_PROCESS_STATUS_CRITICAL')} />;
        case ServerStatusValues.disconnected:
            return <img src={disconnectedStatusIcon} alt="disconnected" title={t('SMC_SERVER_PROCESS_STATUS_DISCONNECTED')} />;
        case ServerStatusValues.missing:
        default:
            return <img src={missingStatusIcon} alt="missing" title={t('SMC_SERVER_PROCESS_STATUS_MISSING')} />;
    }
});

const DnsStatusCell = React.memo(({value, object}) => {
    if (value === ServerStatusValues.ok) {
        return <img src={okStatusIcon} alt="ok" title="DNS server running" />;
    } else if (value === ServerStatusValues.disconnected){
        return <img src={errorStatusIcon} alt="error" title={object[ServerGetListSchema.dnsErrorMessage] || 'Error occured'} />;
    } else {
        return <img src={disabledStatusIcon} alt="disconnected" title="DNS disabled" />
    }
});

export function useServerResults() {
    const { t } = useTranslation();

    const isAdminReadOnly = useSelector(isAdminReadOnlySelector);

    const {
        tableContent,
        isFetching,
        reloadTable,
        deleteObjects
    } = useGenericResultsDataApi(ObjectTypes.SERVER);

    const updatedTableContent = tableContent.map(group => ({
        ...group,
        servers: group.servers.map(server => ({
            ...server,
            isActive: server.isActive ? ServerStatusValues.ok : ServerStatusValues.disconnected
        }))
    }));

    const DeleteCell = React.memo(({object}) => {
        return (
            <SMCIcon
                icon="delete"
                style={{ opacity: isAdminReadOnly ? '0.5' : undefined, cursor: isAdminReadOnly ? 'default' : 'pointer' }}
                onClick={() => {
                    // Don't delete if admin is readonly
                    if (isAdminReadOnly) {
                        return;
                    }
                    if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
                        setIsPageLoaded(false);
                        deleteObjects(object[ServerGetListSchema.name])
                                .then(({successMessage, errorMessage}) => {
                                    successMessage && enqueueSuccessSnackbar(successMessage);
                                    errorMessage && enqueueErrorSnackbar(errorMessage);
                                    reloadTable();
                                })
                                .catch(errorCode => {
                                    enqueueErrorSnackbar(t(errorCode))
                                })
                                .finally(() => {
                                    setIsPageLoaded(true);
                                });
                        }
                    }
                }
            />
        );
    });    


    const serverTableColumns = useMemo(() => ([
        { id: 'name',               accessor: ServerGetListSchema.name,            header: t('SMP.SERVERS.SERVER_NAME'),                            width: 215,                  Cell: NameCell        },
        { id: 'status',             accessor: ServerGetListSchema.status,          header: t('SMP.SERVERS.SERVER_STATUS'),                          width: 110, align: 'center', Cell: StatusCell      },
        { id: 'description',        accessor: ServerGetListSchema.description,     header: '',                                                      width: 20,  align: 'center', Cell: DescriptionCell },
        { id: 'ip_address',         accessor: ServerGetListSchema.ip,              header: t('SMP.SERVERS.IP_ADDRESS'),                             width: 110, align: 'center',                       },
        { id: 'nat_ip_address',     accessor: ServerGetListSchema.natIp,           header: t('SMP.SERVERS.NAT_IP_ADDRESS'),                         width: 115, align: 'center',                       },
        { id: 'dns_name',           accessor: ServerGetListSchema.dnsName,         header: t('SMP.SERVERS.DNS_NAME'),                               width: 200, align: 'center',                       },
        { id: 'is_active',          accessor: ServerGetListSchema.isActive,        header: t('SMP.SERVERS.IS_ACTIVE'),                              width: 110, align: 'center', Cell: StatusCell      },
        { id: 'cpca',               accessor: ServerGetListSchema.cpcaStatus,      header: 'CPCA',                                                  width: 50,  align: 'center', Cell: StatusCell      },
        { id: 'cpd',                accessor: ServerGetListSchema.cpdStatus,       header: 'CPD',                                                   width: 50,  align: 'center', Cell: StatusCell      },
        { id: 'log_server',         accessor: ServerGetListSchema.logServerStatus, header: 'Log Server',                                            width: 90,  align: 'center', Cell: StatusCell      },
        { id: 'smp',                accessor: ServerGetListSchema.smpStatus,       header: 'SMP',                                                   width: 50,  align: 'center', Cell: StatusCell      },
        { id: 'dns',                accessor: ServerGetListSchema.dnsStatus,       header: 'DNS',                                                   width: 50,  align: 'center', Cell: DnsStatusCell   },
        { id: 'delete',                                                            header: <SMCIcon icon="delete" />,                               width: 32,  align: 'center', Cell: DeleteCell      },
    ]), [DeleteCell, t]);
    
    const serverTableRowIdSelector = obj => obj[ServerGetListSchema.name];
    
    const {enqueueErrorSnackbar, enqueueSuccessSnackbar} = useSMCSnackbar();
    const [isPageLoaded, setIsPageLoaded] = useState(false);

    const newObjectCallback = useCallback((objectId, errorCode) => {
        if (errorCode) {
            alert(t(errorCode));
        } else if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, [t]);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    // Load table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    return ({
        serverGroups: updatedTableContent,
        isFetching,
        isPageLoaded,
        serverTableColumns,
        serverTableRowIdSelector,
        newObjectCallback,
        reloadButtonOnClick
    });
}